import { GetServerSideProps, InferGetServerSidePropsType } from 'next'

import { CaughtErrorPage, LayoutErrorPage, NotFoundErrorPage } from '@src/components/error-handling/ErrorComponents'
import { PAGE_TYPES } from '@src/constants/page-type'
import { PAGE_VARIANT } from '@src/quince-site-common/src/domains/PageTypes'
import { isMobileDevice } from '@src/quince-site-common/src/helpers/getDeviceTypeForPageRouter'
import { GetSeoMetapageDataProps } from '@src/quince-site-common/src/utils/seo/getSeoMetadata'
import { BundlePageBFFResponse } from '@src/quince-site-pdp/src/domains/BundlePageContext'
import { ProductPageBFFResponse } from '@src/quince-site-pdp/src/domains/ProductPageContext'
import { getServerSideBaseProps } from '@src/server-side/get-server-side-props'
import {
  CATCH_ALL_IGNORE_PREFIXES,
  SERVER_SIDE_RENDER_TIME_HEADER,
  ServerSideProps,
  shouldShowMiniPDP,
} from '@src/utils/page-type.utils'

import MiniPDPPageTemplate from '../[...slug]/page-templates/mini-pdp-page-template'
import ProductAndBundlePageTemplate from '../[...slug]/page-templates/product-and-bundle-page-template'
import Layout from '../layout'

export const getServerSideProps = (async ctx => {
  const { slug } = ctx.query
  const pathName = typeof slug === 'string' ? slug : Array.isArray(slug) ? slug.join('/') : ''
  if (CATCH_ALL_IGNORE_PREFIXES.some(prefix => pathName.startsWith(prefix))) {
    return {
      props: {
        type: '_ignore_path',
      },
    }
  }

  // Add server time to all SSR requests
  const start = performance.now()
  const serverSideBaseProps = await getServerSideBaseProps(ctx, pathName)
  const end = performance.now()
  const duration = end - start
  ctx.res.setHeader(SERVER_SIDE_RENDER_TIME_HEADER, `${duration}ms`)

  if ('props' in serverSideBaseProps) {
    const props = await serverSideBaseProps.props

    return {
      props: {
        type: '_page',
        ...props,
      },
    }
  }

  return serverSideBaseProps
}) satisfies GetServerSideProps<ServerSideProps>

export default function MiniPDPPage(props: InferGetServerSidePropsType<typeof getServerSideProps>) {
  if (props.type != '_page') {
    return null
  }

  try {
    if (props.pageData) {
      const ssrPageType = props.pageData.context?.ssrPageType

      if (ssrPageType === PAGE_TYPES.PRODUCT_AND_BUNDLE) {
        const showMiniPDP = shouldShowMiniPDP(props)
        const PageTemplate = showMiniPDP ? MiniPDPPageTemplate : ProductAndBundlePageTemplate
        const pageData = {
          ...props.pageData,
          context: {
            ...props.pageData.context,
            pageDataJson: (props.pageData.context?.pageDataJson ?? {}) as
              | ProductPageBFFResponse
              | BundlePageBFFResponse,
            seoData: props.seoMetaData as GetSeoMetapageDataProps | null,
          },
        }
        // PDP and MiniPDP routes share identical URL slugs. Events are differentiated by the pageVariant property.
        const pageVariant = showMiniPDP ? PAGE_VARIANT.MINI_PDP : undefined

        return (
          <Layout {...props} pageVariant={pageVariant} overrideChildrenForNon200={true}>
            <PageTemplate
              pathname={props.pathName}
              pageData={pageData}
              isMobile={isMobileDevice(props.deviceType)}
              serverSideFeatureFlags={props.serverSideFeatureFlags}
            />
          </Layout>
        )
      }
    } else if (props.siteData) {
      return <LayoutErrorPage props={props} />
    }

    return <NotFoundErrorPage props={props} />
  } catch (err) {
    return <CaughtErrorPage error={err} props={props} />
  }
}
